import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { PendingOrderApiAdapter } from '../adapters/PendingOrderApiAdapter';
import { PendingOrder } from '../models/PendingOrder';
import { PendingOrderService } from '../services/PendingOrderService';

// Tipos para el estado local
export interface LocalFilters {
    searchTerm: string;
    courier: string;
    country: string;
    shop: string;
    orderType: string;
    teamName: string;
}

export type GroupingField = 'courier' | 'shop' | 'country' | 'order_type' | 'team_name';

// Tipo extendido para productos agrupados con array de order_ids
export type PendingOrderWithIds = PendingOrder & { order_ids: number[] };

// 🎯 Hook para obtener y procesar pending orders (sin gestión de selección)
export const usePendingOrders = () => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new PendingOrderApiAdapter(httpClient);
    const pendingOrderService = new PendingOrderService(apiAdapter);

    // Estados locales para filtros y agrupación
    const [filters, setFilters] = useState<LocalFilters>({
        searchTerm: '',
        courier: '',
        country: '',
        shop: '',
        orderType: '',
        teamName: ''
    });
    const [groupingFields, setGroupingFields] = useState<Set<GroupingField>>(new Set());

    // Obtener datos de pending orders
    const { data: rawPendingOrders = [], isLoading, isFetching, error, refetch } = useQuery({
        queryKey: ['pendingOrders'],
        queryFn: () => pendingOrderService.getAllPendingOrders(),
        staleTime: 5 * 60 * 1000, // 5 minutos
        gcTime: 10 * 60 * 1000, // 10 minutos
    });

    // 🎯 Total inicial sin filtros (para mostrar botón arriba si es necesario)
    const initialTotal = rawPendingOrders.length;

    // Función para crear una clave única de agrupación
    const getGroupingKey = useCallback((order: PendingOrder, fields: Set<GroupingField>): string => {
        if (fields.size === 0) {
            // Sin agrupación, agrupar por producto
            return `product_${order.product.id}`;
        }

        const keyParts: string[] = [];

        if (fields.has('courier')) keyParts.push(`courier_${order.courier_id}`);
        if (fields.has('shop')) keyParts.push(`shop_${order.shop}`);
        if (fields.has('country')) keyParts.push(`country_${order.country}`);
        if (fields.has('order_type')) keyParts.push(`order_type_${order.order_type}`);
        if (fields.has('team_name')) keyParts.push(`team_name_${order.team_name}`);

        // Siempre incluir el producto para asegurar unicidad
        keyParts.push(`product_${order.product.id}`);

        return keyParts.join('|');
    }, []);

    // Función para aplicar filtros
    const applyFilters = useCallback((orders: PendingOrder[], filters: LocalFilters): PendingOrder[] => {
        return orders.filter(order => {
            // 🎯 Validar que las propiedades del producto existan antes de usar toLowerCase
            const productName = order.product?.name || '';
            const productCode = order.product?.code || '';
            
            const matchesSearch = !filters.searchTerm ||
                productName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                productCode.toLowerCase().includes(filters.searchTerm.toLowerCase());

            const matchesCourier = !filters.courier || order.courier_id === filters.courier;
            const matchesCountry = !filters.country || order.country === filters.country;
            const matchesShop = !filters.shop || order.shop === filters.shop;
            const matchesOrderType = !filters.orderType || order.order_type === filters.orderType;
            const matchesTeamName = !filters.teamName || order.team_name === filters.teamName;

            return matchesSearch && matchesCourier && matchesCountry && matchesShop && matchesOrderType && matchesTeamName;
        });
    }, []);

    // Función para aplicar agrupación
    const applyGrouping = useCallback((orders: PendingOrder[], fields: Set<GroupingField>): PendingOrderWithIds[] => {
        const groupMap = new Map<string, PendingOrderWithIds>();

        orders.forEach(order => {
            const key = getGroupingKey(order, fields);
            const existingOrder = groupMap.get(key);

            if (existingOrder) {
                // Sumar cantidades y acumular order_ids si ya existe la combinación
                existingOrder.qty += order.qty;
                existingOrder.order_ids.push(order.order_id);
            } else {
                // Crear nueva entrada para esta combinación
                groupMap.set(key, {
                    ...order,
                    order_ids: [order.order_id]
                });
            }
        });

        return Array.from(groupMap.values());
    }, [getGroupingKey]);

    // Procesar datos con filtros y agrupación
    const processedOrders = useMemo(() => {
        const filtered = applyFilters(rawPendingOrders, filters);
        const grouped = applyGrouping(filtered, groupingFields);

        // Ordenar por cantidad de mayor a menor
        return grouped.sort((a, b) => b.qty - a.qty);
    }, [rawPendingOrders, filters, groupingFields, applyFilters, applyGrouping]);

    // Obtener valores únicos para los filtros
    const uniqueValues = useMemo(() => {
        return {
            couriers: [...new Set(rawPendingOrders.map(order => order.courier_id))].sort(),
            countries: [...new Set(rawPendingOrders.map(order => order.country))].sort(),
            shops: [...new Set(rawPendingOrders.map(order => order.shop))].sort(),
            orderTypes: [...new Set(rawPendingOrders.map(order => order.order_type))].sort(),
            teamNames: [...new Set(rawPendingOrders.map(order => order.team_name))].sort(),
        };
    }, [rawPendingOrders]);

    // Obtener estadísticas para las agrupaciones
    const groupingStats = useMemo(() => {
        return {
            couriers: new Set(rawPendingOrders.map(order => order.courier_id)).size,
            shops: new Set(rawPendingOrders.map(order => order.shop)).size,
            countries: new Set(rawPendingOrders.map(order => order.country)).size,
            orderTypes: new Set(rawPendingOrders.map(order => order.order_type)).size,
            teamNames: new Set(rawPendingOrders.map(order => order.team_name)).size,
        };
    }, [rawPendingOrders]);

    // Funciones para actualizar filtros
    const updateFilters = useCallback((newFilters: Partial<LocalFilters>) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters({
            searchTerm: '',
            courier: '',
            country: '',
            shop: '',
            orderType: '',
            teamName: ''
        });
    }, []);

    // Funciones para manejar agrupación
    const toggleGrouping = useCallback((field: GroupingField) => {
        setGroupingFields(prev => {
            const newSet = new Set(prev);
            if (newSet.has(field)) {
                newSet.delete(field);
            } else {
                newSet.add(field);
            }
            return newSet;
        });
    }, []);

    const clearGroupings = useCallback(() => {
        setGroupingFields(new Set());
    }, []);

    // Función para obtener descripción de agrupaciones activas
    const getGroupingDescription = useCallback((): string => {
        if (groupingFields.size === 0) return 'productos únicos';

        const descriptions = {
            courier: 'mensajería',
            shop: 'tienda',
            country: 'país',
            order_type: 'tipo de pedido',
            team_name: 'cliente'
        };

        const activeDescriptions = Array.from(groupingFields).map(field => descriptions[field]);

        if (activeDescriptions.length === 1) {
            return `por ${activeDescriptions[0]}`;
        } else if (activeDescriptions.length === 2) {
            return `por ${activeDescriptions.join(' y ')}`;
        } else {
            return `por ${activeDescriptions.slice(0, -1).join(', ')} y ${activeDescriptions[activeDescriptions.length - 1]}`;
        }
    }, [groupingFields]);

    return {
        // Datos procesados
        pendingOrders: processedOrders,
        rawPendingOrders,

        // Estados de la petición
        isLoading,
        isFetching,
        error,
        refetch,

        // Estadísticas
        total: processedOrders.length,
        totalRaw: rawPendingOrders.length,
        initialTotal,

        // Filtros
        filters,
        updateFilters,
        clearFilters,
        uniqueValues,

        // Agrupación
        groupingFields,
        toggleGrouping,
        clearGroupings,
        groupingStats,
        getGroupingDescription,
    };
}; 